/**
 * Feature Flag Provider Component
 * Provider-agnostic wrapper that can work with any feature flag adapter
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import {
	FeatureFlagProviderProps,
	FeatureFlagManager,
	FeatureFlagProvider,
	FeatureFlagConfigWithContext
} from '../types'
import { PostHogAdapter } from '../adapters/posthog-adapter'
import { useAutomaticFeatureFlagContext } from '../hooks/useFeatureFlagContext'
import { EvaluationContext } from 'api-models'

// Create context for feature flag manager
const FeatureFlagContext = createContext<FeatureFlagManager | null>(null)

export const FeatureFlagProviderComponent: React.FC<
	FeatureFlagProviderProps
> = ({ config, children }) => {
	const [manager, setManager] = useState<FeatureFlagManager | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const [error, setError] = useState<Error | undefined>(undefined)

	// Get automatic context from stores
	const automaticContext = useAutomaticFeatureFlagContext()

	// Get PostHog instance if using PostHog provider
	const posthog = usePostHog()

	useEffect(() => {
		const initializeManager = async () => {
			try {
				if (!config.enabled) {
					setManager(null)
					setIsInitialized(true)
					return
				}

				// For PostHog, wait until PostHog is initialized
				if (config.provider === 'posthog' && !posthog) {
					return
				}

				let provider: FeatureFlagProvider | undefined = undefined
				const configWithContext: FeatureFlagConfigWithContext = {
					...config,
					context: {
						...automaticContext
					}
				}

				if (config.provider === 'posthog') {
					if (!posthog) {
						return
					}
					const posthogAdapter = new PostHogAdapter()
					provider = posthogAdapter.createProvider(configWithContext)
					// Set PostHog instance in the provider wrapper
					if (provider && 'setPostHogInstance' in provider) {
						;(provider as any).setPostHogInstance(posthog)
					}
					// Update PostHog context
					if (automaticContext.userId) {
						posthog.identify(automaticContext.userId, {
							email: automaticContext.email,
							accountId: automaticContext.accountId,
							...automaticContext.attributes
						})
					}
				} else {
					console.warn(`Unknown provider: ${config.provider}`)
					provider = undefined
				}

				if (provider) {
					await provider.initialize(configWithContext)

					const manager: FeatureFlagManager = {
						isEnabled: (flagName: string) =>
							provider?.isEnabled(flagName) ?? false,
						getVariant: (flagName: string) =>
							provider?.getVariant(flagName) ?? {
								enabled: false,
								name: 'control',
								payload: {}
							},
						updateContext: (context: Partial<EvaluationContext>) =>
							provider?.updateContext(context) ??
							(() => {
								// do nothing
							}),
						getFlagsStatus: () => ({
							flagsReady: isInitialized,
							flagsError: error
						})
					}

					setManager(manager)
					setIsInitialized(true)
					setError(undefined)
				}
			} catch (err) {
				setError(err instanceof Error ? err : new Error('Unknown error'))
				console.error('Feature flag initialization error:', err)
				setIsInitialized(true)
			}
		}

		initializeManager()
	}, [config, automaticContext, posthog])

	// Update context when it changes
	useEffect(() => {
		if (manager && isInitialized) {
			manager.updateContext(automaticContext)
		}
	}, [automaticContext, manager, isInitialized])

	// Provide the context
	return (
		<FeatureFlagContext.Provider value={manager}>
			{children}
		</FeatureFlagContext.Provider>
	)
}

// Hook to use feature flag context
export const useFeatureFlagContext = (): FeatureFlagManager => {
	const context = useContext(FeatureFlagContext)
	if (!context) {
		// Return a default manager that always returns false/control
		return {
			isEnabled: () => false,
			getVariant: () => ({ enabled: false, name: 'control', payload: {} }),
			updateContext: () => {
				// do nothing
			},
			getFlagsStatus: () => ({ flagsReady: false, flagsError: undefined })
		}
	}
	return context
}
